import { safeFetchAPI } from '@/api';
import { log, utils } from '@/helpers';
import {
	CoMarketingEditRequestWithTemplateAndCampaign,
	GetMostRecentCoMarketingEditReqFilters,
	InstalledCoMarketingTemplateWithCampaign,
	Paginated,
	Pagination,
	SponsoredCoMarketingTemplateWithCampaign,
} from '@/types';
import { create } from 'zustand';

type Paginator = Pagination | ((pagination: Pagination) => Pagination);

export type NeedsAttentionStoreState = {
	loaded: boolean;
	loading: boolean;
	editRequests: CoMarketingEditRequestWithTemplateAndCampaign[];
	waitingToBeApprovedTemplates: InstalledCoMarketingTemplateWithCampaign[];
	sponsoredTemplates: SponsoredCoMarketingTemplateWithCampaign[];

	combinedItems: CombinedItem[];
} & Pagination;

type CombinedItem = CoMarketingEditRequestWithTemplateAndCampaign | InstalledCoMarketingTemplateWithCampaign | SponsoredCoMarketingTemplateWithCampaign;

export type NeedsAttentionStoreActions = {
	initialize: () => Promise<void>;

	fetchOpenEditRequests: (pagination?: Paginator, init?: boolean) => Promise<CoMarketingEditRequestWithTemplateAndCampaign[]>;
	fetchWaitingToBeApprovedTemplates: (pagination?: Paginator, init?: boolean) => Promise<InstalledCoMarketingTemplateWithCampaign[]>;
	fetchSponsoredTemplates: (pagination?: Paginator, init?: boolean) => Promise<SponsoredCoMarketingTemplateWithCampaign[]>;
	nextPage: () => void;
	previousPage: () => void;
};

export type NeedsAttentionStore = NeedsAttentionStoreState & NeedsAttentionStoreActions;

const initialState: NeedsAttentionStoreState = {
	loaded: false,
	loading: false,
	editRequests: [],
	waitingToBeApprovedTemplates: [],
	sponsoredTemplates: [],
	page: 1,
	limit: 10,
	totalEntries: 0,
	combinedItems: [],
};

export const useNeedsAttentionStore = create<NeedsAttentionStore>((set, get) => ({
	...initialState,
	initialize: async () => {
		if (get().loading) {
			return;
		}

		set({ loading: true });

		const editRequests = await get().fetchOpenEditRequests(undefined, true);
		const waitingToBeApprovedTemplates = await get().fetchWaitingToBeApprovedTemplates(undefined, true);
		const sponsoredTemplates = await get().fetchSponsoredTemplates(undefined, true);

		const combinedItems = ([...editRequests, ...waitingToBeApprovedTemplates, ...sponsoredTemplates] as CombinedItem[]).sort((a, b) => {
			return new Date(b.updated).getTime() - new Date(a.updated).getTime();
		});

		set({ combinedItems, loaded: true, loading: false });
	},
	fetchOpenEditRequests: async (pagination?: Paginator, init?: boolean) => {
		const current = { page: get().page, limit: get().limit };
		const paginator = (typeof pagination === 'function' ? pagination({ page: current.page, limit: current.limit }) : pagination) ?? current;
		if (!init) {
			set({ loading: true });
		}
		const [changeRequests, error] = await safeFetchAPI<Paginated<CoMarketingEditRequestWithTemplateAndCampaign>>('/co-marketing/installed/edit/:uid/all', {
			method: 'POST',
			payload: {
				includeCampaign: true,
				page: paginator.page || 1,
				limit: paginator.limit || 10,
				userID: utils.uid,
				antiApprovalStatus: 'approved',
				includeTemplate: true,
			} as GetMostRecentCoMarketingEditReqFilters,
		});
		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching sponsored templates',
				type: 'danger',
			});
			if (!init) {
				set({ loading: false });
			}
			return [];
		}

		const editRequests = changeRequests?.data ?? get().editRequests;
		set({ ...paginator, editRequests });
		log('editRequests', editRequests);
		if (!init) {
			set({ loading: false });
		}
		return editRequests;
	},

	fetchWaitingToBeApprovedTemplates: async (pagination?: Paginator, init?: boolean) => {
		const current = { page: get().page, limit: get().limit };
		const paginator = (typeof pagination === 'function' ? pagination({ page: current.page, limit: current.limit }) : pagination) ?? current;
		if (!init) {
			set({ loading: true });
		}
		const [waitingToBeApproved, error] = await safeFetchAPI<Paginated<InstalledCoMarketingTemplateWithCampaign>>('/co-marketing/installed/:uid/unactivated', {
			method: 'GET',
			queryParams: {
				includeCampaign: true,
				page: paginator.page || 1,
				limit: paginator.limit || 10,
			} as GetMostRecentCoMarketingEditReqFilters,
		});

		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching waiting to be approved templates',
				type: 'danger',
			});
			if (!init) {
				set({ loading: false });
			}
			return [];
		}
		const waitingToBeApprovedTemplates = waitingToBeApproved?.data ?? get().waitingToBeApprovedTemplates;
		set({ ...paginator, waitingToBeApprovedTemplates });
		log('waitingToBeApproved', waitingToBeApprovedTemplates);
		if (!init) {
			set({ loading: false });
		}
		return waitingToBeApprovedTemplates;
	},

	fetchSponsoredTemplates: async (pagination?: Paginator, init?: boolean) => {
		const current = { page: get().page, limit: get().limit };
		const paginator = (typeof pagination === 'function' ? pagination({ page: current.page, limit: current.limit }) : pagination) ?? current;
		if (!init) {
			set({ loading: true });
		}
		const [templates, error] = await safeFetchAPI<Paginated<SponsoredCoMarketingTemplateWithCampaign>>('/co-marketing/sponsored/:uid', {
			queryParams: {
				paid: true,
				includeCampaign: true,
				page: paginator.page || 1,
				limit: paginator.limit || 10,
			},
		});
		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching sponsored templates',
				type: 'danger',
			});
			if (!init) {
				set({ loading: false });
			}
			return [];
		}
		const sponsoredTemplates = templates?.data ?? get().sponsoredTemplates;
		set({ ...paginator, sponsoredTemplates });
		log('sponsoredTemplates', sponsoredTemplates);
		if (!init) {
			set({ loading: false });
		}
		return sponsoredTemplates;
	},

	nextPage: () => {
		const totalPages = Math.ceil((get().totalEntries ?? 0) / (get().limit ?? 0));
		if (get().page < totalPages) {
			get().fetchOpenEditRequests({ page: get().page + 1 });
		}
	},

	previousPage: () => {
		if (get().page > 1) {
			get().fetchOpenEditRequests({ page: get().page - 1 });
		}
	},
}));
